/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * The segment choice above the plan grid.
 *
 * It sits above the grid rather than inside the cards: changing it rewrites
 * all three cards at once, and a control that lives inside a card cannot say
 * that.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { UnstyledButton } from '@/shared/ui';
import type { PlanSegment } from '@/features/pricing';

const SoloIcon: React.FC = () => (
  <svg
    width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
  >
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21a8 8 0 0 1 16 0" />
  </svg>
);

const AgencyIcon: React.FC = () => (
  <svg
    width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
  >
    <circle cx="9" cy="8" r="3.5" />
    <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
    <path d="M16 4.5a3.5 3.5 0 0 1 0 7" />
    <path d="M18 14a6.5 6.5 0 0 1 3.5 6" />
  </svg>
);

/**
 * Who the plans are for: one broker, or an agency buying seats.
 *
 * This is the choice the removed first step used to ask outright. It is a
 * segmented control rather than two screens because the answer changes the
 * prices in place, and seeing that happen is most of what makes the choice.
 *
 * @param props - The active segment and its change handler.
 * @returns The segmented control.
 */
export const SegmentToggle: React.FC<{
  value: PlanSegment;
  onChange: (next: PlanSegment) => void;
  /** Segments that have something on sale. Defaults to both. */
  segments?: PlanSegment[];
}> = ({ value, onChange, segments }) => {
  const { t } = useTranslation('@pages/onboarding');
  const all: { id: PlanSegment; Icon: React.FC; label: string }[] = [
    { id: 'solo', Icon: SoloIcon, label: t('planChoice.segment.solo') },
    { id: 'agency', Icon: AgencyIcon, label: t('planChoice.segment.agency') },
  ];
  const options = segments ? all.filter((o) => segments.includes(o.id)) : all;
  // One tab is not a choice. At launch the other segment may be entirely
  // unopened, and a control that cannot change anything reads as broken.
  if (options.length < 2) return null;

  return (
    <div
      role="tablist"
      aria-label={t('planChoice.segment.label')}
      style={{
        display: 'flex', gap: 'var(--space-1)', padding: 'var(--space-1)',
        background: 'var(--surface-card)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-lg)',
      }}
    >
      {options.map(({ id, Icon, label }) => {
        const active = value === id;
        return (
          <UnstyledButton
            key={id}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
              height: 48, padding: '0 var(--space-5)', border: 'none',
              borderRadius: 'var(--radius-md)', cursor: 'pointer',
              fontSize: 'var(--text-small)', fontWeight: 'var(--font-bold)',
              background: active ? 'var(--surface-raised)' : 'transparent',
              color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
              boxShadow: active ? 'var(--shadow-sm)' : 'none',
              transition: 'var(--transition-fast)',
            }}
          >
            <Icon />
            {label}
          </UnstyledButton>
        );
      })}
    </div>
  );
};

